import { Checkbox } from '@shared/components/ui/checkbox';
import { Label } from '@shared/components/ui/label';
import { ScrollArea } from '@shared/components/ui/scroll-area';
import { Skeleton } from '@shared/components/ui/skeleton';
import { Tags, ChevronRight, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shared/components/ui/collapsible';
import { Separator } from '@shared/components/ui/separator';
import { useSearchStore } from '@shared/stores/searchStore';

export interface ThemaHierarchy {
  id: string;
  naam: string;
  totalCount: number;
  children: {
    id: string;
    naam: string;
    count: number;
  }[];
}

export interface ThemaFilterProps {
  themaHierarchy: ThemaHierarchy[];
  selectedThemas: string[];
  onToggle: (id: string, name?: string) => void;
  onBulkChange?: (ids: string[], names?: Record<string, string>) => void;
  isLoading: boolean;
  isMobile?: boolean;
}

/**
 * Skeleton placeholder for filter list loading state
 */
function FilterListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2 py-2">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="flex items-center space-x-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-3 w-8" />
        </div>
      ))}
    </div>
  );
}

/**
 * ThemaFilter component for filtering cases by theme categories.
 * Supports hierarchical theme structure with parent/child relationships.
 */
export function ThemaFilter({
  themaHierarchy,
  selectedThemas,
  onToggle,
  onBulkChange,
  isLoading,
  isMobile = false,
}: ThemaFilterProps) {
  // Get expanded state and toggle actions from Zustand store
  const expandedSections = useSearchStore((state) => state.uiState.expandedSections);
  const expandedThemas = useSearchStore((state) => state.uiState.expandedThemas);
  const toggleSection = useSearchStore((state) => state.toggleSection);
  const toggleThema = useSearchStore((state) => state.toggleThema);

  return (
    <>
      <Collapsible
        open={expandedSections.includes('themas')}
        onOpenChange={() => toggleSection('themas')}
      >
        <div className="space-y-3">
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between hover:bg-accent/50 rounded px-2 py-1 transition-colors">
              <span className="text-sm font-medium flex items-center gap-2">
                <Tags className="h-4 w-4" />
                Thema's
              </span>
              {expandedSections.includes('themas') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-2 mt-3 pb-2">
              <ScrollArea className="max-h-48">
                <div className="space-y-1">
                  {isLoading ? (
                    <FilterListSkeleton />
                  ) : themaHierarchy.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Geen thema's beschikbaar
                    </p>
                  ) : (
                    themaHierarchy.map(category => {
                      const categoryKey = category.id;
                      const hasChildren = category.children.length > 0;

                      // Leaf node (no children): simple checkbox + label without chevron
                      // Hide zero-count items for consistent UX with wetsartikelen
                      if (!hasChildren) {
                        if (category.totalCount === 0) return null;
                        return (
                          <div
                            key={categoryKey}
                            className="flex items-center space-x-2 py-1"
                          >
                            <Checkbox
                              id={`${isMobile ? 'mobile-' : ''}thema-leaf-${categoryKey}`}
                              checked={selectedThemas.includes(categoryKey)}
                              onCheckedChange={() => onToggle(categoryKey, category.naam)}
                            />
                            <Label
                              htmlFor={`${isMobile ? 'mobile-' : ''}thema-leaf-${categoryKey}`}
                              className="text-sm font-medium flex-1 flex justify-between items-center min-w-0 cursor-pointer"
                            >
                              <span className="truncate" title={category.naam}>{category.naam}</span>
                              <span className="text-muted-foreground text-xs ml-2 shrink-0">({category.totalCount})</span>
                            </Label>
                          </div>
                        );
                      }

                      // Parent node (with children): collapsible with parent checkbox
                      const isCategoryExpanded = expandedThemas.includes(categoryKey);

                      // Only consider visible (non-zero count) children for checkbox state
                      const visibleChildren = category.children.filter(c => c.count > 0);
                      const allChildIds = visibleChildren.map(c => c.id);
                      const allSelected = allChildIds.length > 0 && allChildIds.every(id => selectedThemas.includes(id));
                      const someSelected = allChildIds.some(id => selectedThemas.includes(id));

                      return (
                        <Collapsible
                          key={categoryKey}
                          open={isCategoryExpanded}
                          onOpenChange={() => toggleThema(categoryKey)}
                        >
                          <div className="flex items-center space-x-2 py-1">
                            <Checkbox
                              id={`${isMobile ? 'mobile-' : ''}thema-category-${categoryKey}`}
                              checked={allSelected ? true : someSelected ? "indeterminate" : false}
                              onCheckedChange={(checked) => {
                                // Select or deselect all children in this category
                                if (onBulkChange) {
                                  // Use bulk update if available
                                  const newSelection = checked
                                    ? [...new Set([...selectedThemas, ...allChildIds])]
                                    : selectedThemas.filter(id => !allChildIds.includes(id));

                                  // Build name mapping for new selections
                                  const nameMapping: Record<string, string> = {};
                                  if (checked) {
                                    visibleChildren.forEach(child => {
                                      nameMapping[child.id] = child.naam;
                                    });
                                  }

                                  onBulkChange(newSelection, nameMapping);
                                } else {
                                  // Fall back to individual toggles
                                  const idsToToggle = checked
                                    ? allChildIds.filter(id => !selectedThemas.includes(id))
                                    : allChildIds.filter(id => selectedThemas.includes(id));

                                  idsToToggle.forEach(id => {
                                    const child = category.children.find(c => c.id === id);
                                    setTimeout(() => onToggle(id, child?.naam), 0);
                                  });
                                }
                              }}
                            />
                            <CollapsibleTrigger asChild>
                              <button className="flex items-center flex-1 min-w-0 text-left hover:bg-accent/50 rounded px-1 py-0.5 transition-colors">
                                {isCategoryExpanded ? (
                                  <ChevronDown className="h-3 w-3 shrink-0 mr-1" />
                                ) : (
                                  <ChevronRight className="h-3 w-3 shrink-0 mr-1" />
                                )}
                                <span className="text-sm font-medium truncate" title={category.naam}>{category.naam}</span>
                                <span className="text-muted-foreground text-xs ml-auto shrink-0">({category.totalCount})</span>
                              </button>
                            </CollapsibleTrigger>
                          </div>

                          <CollapsibleContent className="ml-4 space-y-1 mt-1">
                            {category.children.filter(child => child.count > 0).map(child => (
                              <div
                                key={`${categoryKey}-${child.id}`}
                                className="flex items-center space-x-2 py-1"
                              >
                                <Checkbox
                                  id={`${isMobile ? 'mobile-' : ''}thema-child-${child.id}`}
                                  checked={selectedThemas.includes(child.id)}
                                  onCheckedChange={() => onToggle(child.id, child.naam)}
                                />
                                <Label
                                  htmlFor={`${isMobile ? 'mobile-' : ''}thema-child-${child.id}`}
                                  className="text-sm font-normal flex-1 flex justify-between items-center min-w-0 cursor-pointer"
                                >
                                  <span className="truncate" title={child.naam}>{child.naam}</span>
                                  <span className="text-muted-foreground text-xs ml-2 shrink-0">({child.count})</span>
                                </Label>
                              </div>
                            ))}
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      <Separator />
    </>
  );
}
